import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BrandLogo } from "@/components/brand/Logo";
import GoogleButton from "@/components/auth/GoogleButton";
import { User, Building2, ArrowLeft } from "lucide-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";

function formatError(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e.msg || JSON.stringify(e)).join(", ");
  return String(d);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmail(value) {
  return EMAIL_RE.test(value);
}

function isPhoneish(value) {
  if (value.includes("@")) return false;
  const digits = value.replace(/[^\d]/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

export default function Login() {
  const { login, register, checkEmailExists, requestOtp, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [step, setStep] = useState("identifier"); // identifier | password | register | otp
  const [loading, setLoading] = useState(false);

  const [identifier, setIdentifier] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");

  const afterAuth = (u) => {
    toast("Welcome to Nirvagoo");
    const dest = params.get("redirect");
    if (dest) nav(dest);
    else nav(u.role === "admin" ? "/admin" : u.role === "owner" ? "/owner" : "/dashboard");
  };

  const onContinue = async (e) => {
    e.preventDefault();
    const value = identifier.trim();
    if (!value) return;
    setLoading(true);
    try {
      if (isEmail(value)) {
        const exists = await checkEmailExists(value);
        setEmail(value);
        setStep(exists ? "password" : "register");
      } else if (isPhoneish(value)) {
        const data = await requestOtp(value);
        setPhone(value);
        setCode("");
        setDevOtp(data.devOtp || "");
        setStep("otp");
        if (data.devOtp) toast(`Dev mode — your code is ${data.devOtp}`);
      } else {
        toast("Enter a valid phone number or email");
      }
    } catch (err) {
      toast(formatError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      afterAuth(u);
    } catch (err) {
      toast(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(name, email, password, role);
      afterAuth(u);
    } catch (err) {
      toast(formatError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verifyOtp(phone, code);
      afterAuth(u);
    } catch (err) {
      toast(formatError(err.response?.data?.detail) || "Incorrect code");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      const data = await requestOtp(phone);
      setDevOtp(data.devOtp || "");
      toast(data.devOtp ? `Dev mode — your code is ${data.devOtp}` : "Code sent");
    } catch (err) {
      toast(formatError(err.response?.data?.detail) || "Could not resend code");
    }
  };

  const backToIdentifier = () => {
    setStep("identifier");
    setPassword("");
    setCode("");
  };

  return (
    <Box
      data-testid="login-page"
      sx={{ display: "flex", justifyContent: "center", py: { xs: 6, md: 10 }, px: 2, minHeight: "80vh" }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 4,
          boxShadow: 3,
          border: 1,
          borderColor: "divider",
          p: { xs: 3, sm: 4.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          alignSelf: "flex-start",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {step !== "identifier" && (
            <IconButton onClick={backToIdentifier} size="small" data-testid="login-back" sx={{ ml: -1 }}>
              <ArrowLeft size={18} />
            </IconButton>
          )}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <BrandLogo size={26} />
          </Box>
          {step !== "identifier" && <Box sx={{ width: 34 }} />}
        </Box>

        {step === "identifier" && (
          <Box component="form" onSubmit={onContinue} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography align="center" sx={{ fontSize: "1.375rem", fontWeight: 600 }}>
              Log in or sign up
            </Typography>

            <TextField
              autoFocus
              label="Phone number or email"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              inputProps={{ "data-testid": "login-identifier" }}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              We'll text you a code, or check for an existing account.
            </Typography>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              data-testid="login-continue"
              sx={{ py: 1.25, borderRadius: 999 }}
              fullWidth
            >
              {loading ? "Please wait…" : "Continue"}
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 0.5 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary">or</Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            <GoogleButton onSuccess={afterAuth} onError={(err) => toast(err.message || "Google sign-in failed")} />
          </Box>
        )}

        {step === "password" && (
          <Box component="form" onSubmit={submitPassword} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography align="center" sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
              Welcome back
            </Typography>
            <Typography align="center" variant="body2" color="text.secondary">{email}</Typography>

            <TextField
              autoFocus
              type="password"
              required
              label="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              inputProps={{ "data-testid": "login-password" }}
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              data-testid="login-submit"
              sx={{ py: 1.25, borderRadius: 999 }}
              fullWidth
            >
              {loading ? "Signing in…" : "Log in"}
            </Button>
          </Box>
        )}

        {step === "register" && (
          <Box component="form" onSubmit={submitRegister} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography align="center" sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
              Create your account
            </Typography>
            <Typography align="center" variant="body2" color="text.secondary">{email}</Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              <ButtonBase
                type="button"
                onClick={() => setRole("user")}
                data-testid="role-user"
                sx={{
                  p: 1.5, borderRadius: 2.5,
                  border: role === "user" ? "2px solid" : "none",
                  borderColor: role === "user" ? "primary.main" : "transparent",
                  boxShadow: 1,
                  bgcolor: role === "user" ? (theme) => alpha(theme.palette.primary.main, 0.05) : "transparent",
                  textAlign: "left", justifyContent: "flex-start", flexDirection: "column", alignItems: "flex-start", gap: 0.5,
                }}
              >
                <User size={14} color={role === "user" ? "#C5A059" : "currentColor"} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Guest</Typography>
              </ButtonBase>
              <ButtonBase
                type="button"
                onClick={() => setRole("owner")}
                data-testid="role-owner"
                sx={{
                  p: 1.5, borderRadius: 2.5,
                  border: role === "owner" ? "2px solid" : "none",
                  borderColor: role === "owner" ? "primary.main" : "transparent",
                  boxShadow: 1,
                  bgcolor: role === "owner" ? (theme) => alpha(theme.palette.primary.main, 0.05) : "transparent",
                  textAlign: "left", justifyContent: "flex-start", flexDirection: "column", alignItems: "flex-start", gap: 0.5,
                }}
              >
                <Building2 size={14} color={role === "owner" ? "#C5A059" : "currentColor"} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Villa owner</Typography>
              </ButtonBase>
            </Box>

            <TextField
              autoFocus
              required
              label="Full name"
              value={name}
              onChange={e => setName(e.target.value)}
              inputProps={{ "data-testid": "reg-name" }}
              fullWidth
            />
            <TextField
              type="password"
              required
              inputProps={{ minLength: 6, "data-testid": "reg-password" }}
              label="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              data-testid="reg-submit"
              sx={{ py: 1.25, borderRadius: 999 }}
              fullWidth
            >
              {loading ? "Creating…" : "Agree and continue"}
            </Button>
          </Box>
        )}

        {step === "otp" && (
          <Box component="form" onSubmit={submitOtp} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography align="center" sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
              Enter the code
            </Typography>
            <Typography align="center" variant="body2" color="text.secondary">
              Sent to {phone}
            </Typography>
            {devOtp && (
              <Typography align="center" variant="caption" sx={{ color: "primary.main", fontWeight: 600 }}>
                Dev mode: your code is {devOtp}
              </Typography>
            )}

            <TextField
              autoFocus
              required
              label="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6, "data-testid": "login-otp-code" }}
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || code.length < 4}
              data-testid="login-otp-verify"
              sx={{ py: 1.25, borderRadius: 999 }}
              fullWidth
            >
              {loading ? "Verifying…" : "Verify"}
            </Button>

            <Button type="button" onClick={resendOtp} variant="text" size="small" sx={{ alignSelf: "center" }}>
              Resend code
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
